import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Clock,
  MapPin,
  Send,
  CheckCircle,
  AlertCircle,
  Instagram,
  ChevronDown
} from 'lucide-react'
import { Link } from 'react-router-dom'
import Seo from '../components/Seo'

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
    // Honeypot — real visitors never see or fill this field; a bot that
    // fills every input will, and the server rejects the submission.
    company: '',
  })
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')
  const [openFaq, setOpenFaq] = useState<number | null>(0)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setSuccess(false)

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      })
      const result = await response.json().catch(() => null)

      if (!response.ok || !result?.ok) {
        throw new Error(result?.error || 'Message could not be sent.')
      }

      setSuccess(true)
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        message: '',
        company: '',
      })
    } catch (err) {
      setError(
        'Sorry — your message did not go through. Please try again, or email hello@fitbycassie.com directly.'
      )
    } finally {
      setLoading(false)
    }
  }

const services = [
  'The Strong Again Method™',
  'Nutrition Blueprint',
  'Physique & Competition Coaching',
  'DMV In-Person Coaching Inquiry (Optional Add-On)',
  'Partnership / Collaboration',
  'General Question',
  'Not Sure Yet'
]
  const faqs: { question: string; answer: React.ReactNode }[] = [
  {
    question: 'How do I get started?',
    answer: (
      <>
        Start by{' '}
        <Link
          to="/book-consultation"
          className="text-brand font-semibold hover:text-brand-dark"
        >
          scheduling a complimentary 30-minute consultation
        </Link>
        . You'll tell me about your goals, challenges, experience, and the kind of support you're looking for. I'll help you determine which coaching option is the best fit and explain the next steps.
      </>
    ),
  },
  {
    question: 'Which coaching program is right for me?',
    answer:
      "That depends on your goals and the kind of support you're looking for. The Strong Again Method™ combines strength, nutrition, and ongoing coaching. Nutrition Blueprint focuses on your nutrition. Physique & Competition Coaching is for more advanced physique or competition goals. We can talk through the options during your complimentary consultation.",
  },
  {
    question: 'Is coaching available online or in person?',
    answer:
      'Fit by Cassie is an online-first coaching business. You can follow your individualized program at home or at a gym you belong to, with guidance through your coaching materials and the phone or video support included in your program. Select in-person sessions may be available in the DMV area for an additional fee.',
  },
  {
    question: 'What happens after my consultation?',
    answer:
      "We'll discuss your goals, experience, starting point, and the support you're looking for. If there's a good fit, I'll explain the recommended coaching option, investment, and next steps. Once you're enrolled, you'll complete your onboarding documents so I can prepare your individual coaching plan.",
  },
  {
    question: 'What is the investment for coaching?',
    answer: (
      <>
        Founding-client pricing starts at $347 per month for The Strong Again Method™ with a three-month minimum, and $349 for the 12-week Nutrition Blueprint. Physique & Competition Coaching varies by program and duration, starting at $749. You can review all current founding-client rates on the{' '}
        <Link
          to="/coaching"
          className="text-brand font-semibold hover:text-brand-dark"
        >
          Coaching &amp; Investment
        </Link>
        {' '}page before scheduling your consultation.
      </>
    ),
  },
]
  return (
    <div className="min-h-screen">
      <Seo
        title="Contact Cassie"

        description="Contact Cassie about coaching, nutrition, partnerships, or general questions. Ready for coaching? Schedule a complimentary 30-minute consultation."
        image="/images/cassie-contact.webp"
      />

      {/* Contact form + info panel */}
      <section className="section-padding bg-brand-ivory relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] bg-brand/5 rounded-full blur-3xl pointer-events-none" aria-hidden />
        <div className="container-max relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-10 sm:mb-14"
          >
            <span className="eyebrow-blush justify-center mb-4">Contact</span>
            <h1 className="heading-display mb-4 sm:mb-5">
              Let's talk about what's <em className="italic text-brand">possible for you</em>
            </h1>

            <p className="lead max-w-2xl mx-auto">
  Have a question about coaching, a partnership, or something else? Send me a
  message below. Ready to talk about your goals?{' '}
  <Link to="/book-consultation" className="text-brand font-semibold hover:text-brand-dark">
    Schedule your complimentary consultation
  </Link>
  , and we'll start with where you are right now.
</p>          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-10 items-start">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-3"
            >
              <div className="bg-white rounded-3xl shadow-soft p-5 sm:p-10 border border-gray-100">
                <h2 className="font-display text-2xl font-semibold text-ink mb-7">
                  Send a Message
                </h2>

                {success && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-brand-soft border border-brand/20 text-brand-dark px-4 py-3 rounded-xl mb-6 flex items-center gap-2"
                  >
                    <CheckCircle size={20} />

<span>
Thank you! Your message has been received. You can typically expect a response within 24–48 hours.
</span>
                  </motion.div>
                )}

                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6 flex items-start gap-2"
                  >
                    <AlertCircle size={20} className="shrink-0 mt-0.5" />
                    <span>{error}</span>
                  </motion.div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Honeypot field — hidden from sighted users and screen readers,
                      and off the natural tab order, so only a bot fills it in. */}
                  <div className="absolute -left-[9999px] w-px h-px overflow-hidden" aria-hidden="true">
                    <label htmlFor="company">Company</label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      tabIndex={-1}
                      autoComplete="off"
                      value={formData.company}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="form-label">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="Your full name"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="form-label">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="phone" className="form-label">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                    <div>
                      <label htmlFor="service" className="form-label">
                        Service Interest
                      </label>
                      <select
                        id="service"
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        className="form-input"
                      >
                        <option value="">Select a service</option>
                        {services.map((service) => (
                          <option key={service} value={service}>
                            {service}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="form-label">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      className="form-input resize-none"
                      placeholder="Tell me a little about what's on your mind."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full btn-primary !py-4 text-lg"
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    ) : (
                      <>
                        Send Message
                        <Send className="ml-2" size={19} />
                      </>
                    )}
                  </button>

                  <p className="text-center text-sm text-gray-500">
                    Already booked a consultation?{' '}
                    <Link
                      to="/manage-consultation"
                      className="font-semibold underline underline-offset-2 text-brand hover:text-brand-dark"
                    >
                      Manage My Consultation
                    </Link>
                    {' '}to change your preferred time or cancel your request.
                  </p>
                </form>
              </div>
            </motion.div>

            {/* Socials + hours panel */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="lg:col-span-2"
            >
              <div className="bg-brand-navy text-white rounded-3xl shadow-soft-lg overflow-hidden">
                <div className="relative aspect-[4/5]">
                  <img
                    src="/images/cassie-contact.webp"
                    alt="Cassie smiling in studio, ready to help you start your fitness journey"
                    className="absolute inset-0 w-full h-full object-contain object-bottom"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-navy via-brand-navy/25 to-transparent pointer-events-none" />
                </div>

                <div className="p-8 -mt-6 relative">
                  <h3 className="font-display text-2xl font-semibold mb-6">
                    Connect with Cassie
                  </h3>

                  {/* Coaching Availability */}
<div className="mb-6">
  <div className="flex items-center gap-3 mb-2">
    <MapPin size={17} className="text-brand-bronze" />
    <span className="text-sm font-semibold uppercase tracking-widest-xl text-gray-300">
      Online Coaching
    </span>
  </div>
  <p className="text-white pl-8">Train at home, at a gym you belong to, or wherever your program fits your life.</p>

<p className="text-gray-300 text-sm pl-8 mt-2">
  Select in-person sessions may be available as an optional add-on for clients in the DC, Maryland &amp; Northern Virginia area, subject to location and availability, for an additional fee.
</p>

</div>

{/* Response Time */}
<div className="border-t border-white/10 pt-6 mb-6">
  <div className="flex items-center gap-3 mb-2">
    <Clock size={17} className="text-brand-bronze" />
    <span className="text-sm font-semibold uppercase tracking-widest-xl text-gray-300">
      Response Time
    </span>
  </div>
<p className="text-white pl-8">Typically within 24–48 hours</p>
  <p className="text-gray-300 text-sm pl-8">
  Monday–Sunday · Eastern Time (ET)
</p>  
</div>                  
{/* Socials */}
<div className="border-t border-white/10 pt-6">
  <div className="flex items-center gap-3 mb-3">
    <Instagram size={17} className="text-brand-bronze" />
    <span className="text-sm font-semibold uppercase tracking-widest-xl text-gray-300">
      Follow Fit by Cassie
    </span>
  </div>

  <div className="pl-8 space-y-2">
    <a
      href="https://www.instagram.com/fitbycassie_/"
      target="_blank"
      rel="noopener noreferrer"
      className="block text-gray-300 hover:text-brand-bronze transition-colors"
    >
      Instagram · @fitbycassie_
    </a>

    <a
      href="https://www.facebook.com/share/19bfrrLqmv/?mibextid=wwXIfr"
      target="_blank"
      rel="noopener noreferrer"
      className="block text-gray-300 hover:text-brand-bronze transition-colors"
    >
 Facebook · Cassie Tach
    </a>
  </div>
</div>                  
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ — accordions */}
      <section id="faq" className="section-padding bg-brand-navy">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-14"
          >
            <span className="inline-flex items-center gap-3 text-xs font-semibold uppercase tracking-widest-xl text-brand-blush justify-center mb-4">
              Good to Know
            </span>
            <h2 className="font-display text-[1.75rem] sm:text-4xl lg:text-5xl font-semibold leading-[1.12] text-brand-cream mb-5">
              Frequently Asked <em className="italic text-brand-blush">Questions</em>
            </h2>

<p className="text-base sm:text-lg lg:text-xl text-brand-cream/70 leading-relaxed max-w-2xl mx-auto">
  Everything you need to know about choosing a program, working with Cassie, and getting started.
</p>
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={faq.question}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.08 }}
                className="rounded-2xl border border-gray-100 bg-white shadow-soft overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between gap-4 px-7 py-5 text-left"
                  aria-expanded={openFaq === index}
                >
                  <span className="font-semibold text-ink text-lg">
                    {faq.question}
                  </span>
                  <ChevronDown
                    size={20}
                    className={`shrink-0 text-brand transition-transform duration-300 ${openFaq === index ? 'rotate-180' : ''}`}
                  />
                </button>
                <AnimatePresence initial={false}>
                  {openFaq === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                    >
                      <p className="px-7 pb-6 text-gray-600 leading-relaxed">
                        {faq.answer}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}

export default Contact
